<template>
  <div class="insights">
    <header class="insights__header">
      <h1 class="insights__title">Insights</h1>
      <p class="insights__subtitle">
        See how much of your bookkeeping has been handled automatically, how confident the
        suggestions were, and how often they matched what your team actually posted.
      </p>

      <q-expansion-item
        class="insights__glossary"
        icon="help_outline"
        label="How to read this page"
        dense
        :default-opened="false"
      >
        <div class="insights__glossary-body">
          <p class="insights__glossary-lead">A few terms used across the page:</p>
          <dl>
            <dt>Invoice</dt>
            <dd>One document received from a supplier — typically a PDF or e-invoice. An
              invoice may have several lines on it (e.g. one line per product, fee or
              charge).</dd>
            <dt>Proposal</dt>
            <dd>A suggestion for how an invoice (or one of its lines) should be coded — which
              account, department, project, etc. Proposals come with a confidence level so
              your team can see at a glance which ones are safe and which need a second look.</dd>
            <dt>Posting</dt>
            <dd>The actual booking entry that ended up in your accounting system. A posting
              may come from accepting a proposal or from a manual edit. One invoice usually
              produces several postings (one per line).</dd>
            <dt>Confidence level</dt>
            <dd>
              <strong>High</strong> — confident enough to post automatically.
              <strong>Medium</strong> — a bookkeeper should glance at the suggestion.
              <strong>Low</strong> — a bookkeeper should review carefully.
            </dd>
            <dt>Dimension</dt>
            <dd>A field that has to be filled in when posting an invoice line — for example
              <em>Account</em>, <em>Department</em>, <em>Project</em>. The "Combination" row
              is the all-dimensions-correct rate (every field matched at once, not just one
              of them).</dd>
            <dt>Invoice level vs. line level</dt>
            <dd>"Total invoices" counts whole documents. Confidence and accuracy figures are
              calculated per <strong>invoice line</strong>, because each line has its own
              account, department, etc.</dd>
          </dl>
        </div>
      </q-expansion-item>
    </header>

    <section class="insights__scope">
      <InsightsScopeBar
        :show-company-picker="showCompanyPicker"
        :companies="companies"
        :companies-loading="companiesLoading"
        :selected-company="selectedCompany"
        @update:selected-company="onCompanySelected"
        :date-range="dateRange"
        @update:date-range="onDateRangeChanged"
      />
    </section>

    <div v-if="showEmptyTenantState" class="insights__hint insights__hint--empty">
      <q-icon name="inbox" size="20px" color="grey-7" />
      <div>
        <div class="insights__hint-title">No companies set up yet</div>
        <div class="insights__hint-body">
          Once invoice data is ingested for this tenant, the companies will appear here and
          you'll be able to see their insights.
        </div>
      </div>
    </div>

    <div v-else-if="companiesLoadError" class="insights__hint insights__hint--error">
      <q-icon name="error_outline" size="20px" />
      <div class="insights__hint-content">
        <div class="insights__hint-title">Couldn't load the company list</div>
        <div class="insights__hint-body">
          Something went wrong while contacting the server. Your session is still active, so
          you can try again without re-opening this page.
        </div>
        <q-btn
          dense
          flat
          color="negative"
          icon="refresh"
          label="Try again"
          :loading="companiesLoading"
          @click="loadCompanies"
          class="insights__hint-action"
        />
      </div>
    </div>

    <div v-else-if="!effectiveCompany" class="insights__hint">
      <q-icon name="info" size="18px" color="primary" />
      <span>Select a company to view its insights.</span>
    </div>

    <template v-else>
      <section v-if="sections.counts !== false" class="insights__section">
        <InsightsCounts :data="counts" :loading="countsLoading" />
      </section>

      <section v-if="sections.trends !== false" class="insights__section">
        <InsightsTrends :data="trends" :loading="trendsLoading" />
      </section>

      <section v-if="sections.dimensions !== false" class="insights__section">
        <InsightsDimensions :data="dimensions" :loading="dimensionsLoading" />
      </section>

      <section v-if="sections.precision !== false" class="insights__section">
        <InsightsPrecision
          :data="precision"
          :loading="precisionLoading"
          :model-value="precisionPeriod"
          @update:model-value="onPrecisionPeriodChanged"
        />
      </section>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { api } from '@/api/client';
import { useClaimValue, useToken } from '@/composables/useToken';
import InsightsScopeBar from './components/InsightsScopeBar.vue';
import InsightsCounts from './components/InsightsCounts.vue';
import InsightsTrends from './components/InsightsTrends.vue';
import InsightsDimensions from './components/InsightsDimensions.vue';
import InsightsPrecision from './components/InsightsPrecision.vue';
import type {
  InsightsCompany,
  InsightsCountsResult,
  InsightsDateRange,
  InsightsDimensionsResult,
  InsightsPrecisionPeriod,
  InsightsPrecisionResult,
  InsightsSectionToggles,
  InsightsTrendsResult
} from './types';

const { claims } = useToken();

// Both flags below are point-in-time copies of the session claims. They don't change for
// the lifetime of the iframe — a session is bound to its scope at creation time — so we
// don't need to track them reactively beyond the initial read.
const pinnedCompany = useClaimValue<string | null>('companyName', null);
const sections = useClaimValue<InsightsSectionToggles>('sections', {});
const showCompanyPicker = computed(() => !pinnedCompany);

const companies = ref<InsightsCompany[]>([]);
const companiesLoading = ref(false);
const companiesLoaded = ref(false);
const companiesLoadError = ref(false);
const selectedCompany = ref<string | null>(pinnedCompany);

// True only after we've actually heard back from /companies with an empty list. The picker
// is hidden when the session is company-scoped, in which case `companiesLoaded` never flips
// and the empty state correctly stays suppressed.
const showEmptyTenantState = computed(() =>
  !pinnedCompany && companiesLoaded.value && companies.value.length === 0
);

const effectiveCompany = computed(() =>
  pinnedCompany ?? selectedCompany.value
);

const today = () => new Date().toISOString().slice(0, 10);
const monthAgo = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return d.toISOString().slice(0, 10);
};

const dateRange = ref<InsightsDateRange>({ from: monthAgo(), to: today() });

const counts = ref<InsightsCountsResult | null>(null);
const countsLoading = ref(false);

const trends = ref<InsightsTrendsResult | null>(null);
const trendsLoading = ref(false);

const dimensions = ref<InsightsDimensionsResult | null>(null);
const dimensionsLoading = ref(false);

const precision = ref<InsightsPrecisionResult | null>(null);
const precisionLoading = ref(false);
const precisionPeriod = ref<InsightsPrecisionPeriod>('PastMonth');

async function loadCompanies() {
  if (pinnedCompany) return;
  companiesLoadError.value = false;
  try {
    companiesLoading.value = true;
    const { data } = await api.insights.getCompanies();
    companies.value = data;
    companiesLoaded.value = true;
    // Pre-select the first company so the page renders data immediately instead of stranding
    // the user on "Select a company". The picker stays visible and prominent for switching;
    // KauntAnalytics returns companies in a stable order so a refresh lands on the same one.
    if (data.length > 0 && !selectedCompany.value) {
      selectedCompany.value = data[0].companyName;
    }
  } catch {
    // A failed /companies call leaves the picker disabled with no fallback — the user can't
    // pick anything. Surface the error explicitly instead of leaving them on a silent empty
    // page; refreshing is the recovery path.
    companiesLoadError.value = true;
  } finally {
    companiesLoading.value = false;
  }
}

async function loadCounts() {
  if (sections.counts === false) return;
  if (!effectiveCompany.value || !dateRange.value.from || !dateRange.value.to) return;
  // When the session pins a company we don't pass it on the wire — the BFF reads it from
  // claims and rejects mismatches. Sending null lets the request helper omit the param.
  const company = pinnedCompany ? null : effectiveCompany.value;
  try {
    countsLoading.value = true;
    const { data } = await api.insights.getCounts(company, dateRange.value.from, dateRange.value.to);
    counts.value = data;
  } finally {
    countsLoading.value = false;
  }
}

async function loadTrends() {
  if (sections.trends === false) return;
  if (!effectiveCompany.value || !dateRange.value.from || !dateRange.value.to) return;
  const company = pinnedCompany ? null : effectiveCompany.value;
  try {
    trendsLoading.value = true;
    const { data } = await api.insights.getTrends(company, dateRange.value.from, dateRange.value.to);
    trends.value = data;
  } finally {
    trendsLoading.value = false;
  }
}

async function loadDimensions() {
  if (sections.dimensions === false) return;
  if (!effectiveCompany.value || !dateRange.value.from || !dateRange.value.to) return;
  const company = pinnedCompany ? null : effectiveCompany.value;
  try {
    dimensionsLoading.value = true;
    const { data } = await api.insights.getDimensions(company, dateRange.value.from, dateRange.value.to);
    dimensions.value = data;
  } finally {
    dimensionsLoading.value = false;
  }
}

async function loadPrecision() {
  if (sections.precision === false) return;
  if (!effectiveCompany.value) return;
  const company = pinnedCompany ? null : effectiveCompany.value;
  try {
    precisionLoading.value = true;
    const { data } = await api.insights.getPrecision(company, precisionPeriod.value);
    precision.value = data;
  } finally {
    precisionLoading.value = false;
  }
}

function loadDateScoped() {
  loadCounts();
  loadTrends();
  loadDimensions();
}

function onCompanySelected(value: string | null) {
  selectedCompany.value = value;
}

function onDateRangeChanged(value: InsightsDateRange) {
  dateRange.value = value;
}

function onPrecisionPeriodChanged(value: InsightsPrecisionPeriod) {
  precisionPeriod.value = value;
}

watch(effectiveCompany, (next) => {
  if (next) {
    loadDateScoped();
    loadPrecision();
  }
});

watch(dateRange, () => {
  if (effectiveCompany.value) loadDateScoped();
}, { deep: true });

watch(precisionPeriod, () => {
  if (effectiveCompany.value) loadPrecision();
});

// Surface the claims-derived state to satisfy reactivity tracking; useClaimValue snapshots
// at call time so the `claims` ComputedRef isn't otherwise read. Keeping the reference here
// makes the dependency explicit for future refactors.
void claims;

onMounted(async () => {
  await loadCompanies();
  if (effectiveCompany.value) {
    loadDateScoped();
    loadPrecision();
  }
});
</script>

<style scoped lang="scss">
.insights {
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 16px;

  &__header {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__title {
    margin: 0;
    font-size: 28px;
    font-weight: 600;
    color: #2c2c2c;
  }

  &__subtitle {
    margin: 0 0 4px;
    font-size: 14px;
    color: #4c4c4c;
    max-width: 720px;
  }

  &__glossary {
    margin-top: 4px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    background: #fafafa;

    :deep(.q-item) {
      min-height: 36px;
      font-weight: 500;
      color: #4c4c4c;
    }
  }

  &__glossary-body {
    padding: 0 20px 16px 20px;

    dl {
      display: grid;
      grid-template-columns: minmax(160px, 220px) 1fr;
      column-gap: 16px;
      row-gap: 8px;
      margin: 0;
    }

    dt {
      font-weight: 600;
      color: #2c2c2c;
      font-size: 13px;
    }

    dd {
      margin: 0;
      font-size: 13px;
      color: #444;
      line-height: 1.4;
    }
  }

  &__glossary-lead {
    margin: 0 0 12px;
    font-size: 13px;
    color: #4c4c4c;
  }

  &__scope {
    background: white;
    border: 1px solid #e0e0e0;
    border-radius: 10px;
    padding: 16px;
  }

  &__hint {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 16px;
    background: #f1f8ff;
    border: 1px solid #cbe4ff;
    border-radius: 10px;
    color: #1a4d80;
    font-size: 14px;

    &--empty {
      background: #f5f5f5;
      border-color: #d9d9d9;
      color: #4c4c4c;
    }

    &--error {
      background: #fdecea;
      border-color: #f5b5b0;
      color: #8a2218;
    }
  }

  &__hint-title {
    font-weight: 600;
    margin-bottom: 2px;
  }

  &__hint-body {
    font-size: 13px;
    color: inherit;
    opacity: 0.85;
    line-height: 1.4;
  }

  &__hint-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__hint-action {
    align-self: flex-start;
    margin-top: 4px;
  }

  &__section {
    background: white;
    border: 1px solid #e0e0e0;
    border-radius: 10px;
    padding: 20px;
  }
}
</style>
